import { useMemo } from "react";
import type { Threshold } from "diagnostics-domain/dashboard/type";
import t from "../../../../../../../../i18n";

export function useThreshold({
  thresholds,
  thresholdValue,
  annotationValues = [],
}: {
  thresholds?: Threshold[] | null;
  thresholdValue?: number | null;
  annotationValues?: { column: string | null; unit: string | null | undefined }[];
}) {
  const matchingThreshold = useMemo(() => {
    if (!thresholds || thresholds.length === 0) return null;

    if (typeof thresholdValue !== "number" || Number.isNaN(thresholdValue)) {
      return null;
    }

    const matchingGteThreshold = thresholds
      .filter(t => t.comparison === "gte" && t.value !== null)
      .sort((a, b) => (a.value !== null && b.value !== null ? b.value - a.value : 0))
      .find(t => {
        if (t.value === null) return false;
        return t.absolute ? Math.abs(thresholdValue) >= t.value : thresholdValue >= t.value;
      });

    const matchingLteThreshold = thresholds
      .filter(t => t.comparison === "lte" && t.value !== null)
      .sort((a, b) => (a.value !== null && b.value !== null ? a.value - b.value : 0))
      .find(t => {
        if (t.value === null) return false;
        return t.absolute ? Math.abs(thresholdValue) <= t.value : thresholdValue <= t.value;
      });

    return matchingGteThreshold || matchingLteThreshold || null;
  }, [thresholds, thresholdValue]);

  const thresholdColor = useMemo(() => {
    if (!matchingThreshold) return null;
    return matchingThreshold.color ?? null;
  }, [matchingThreshold]);

  const columnUnit = useMemo(() => {
    if (!matchingThreshold?.column) return null;
    const foundValue = annotationValues.find(val => val.column === matchingThreshold.column);
    return foundValue?.unit ?? null;
  }, [matchingThreshold, annotationValues]);

  const thresholdDescription = useMemo(() => {
    if (!matchingThreshold || matchingThreshold.value === null) return null;

    const { value, comparison, absolute, column } = matchingThreshold;

    if (column === "normalizedGap") {
      return t(
        "project.dashboard.block.diagram.above_absolute_normalized_gap",
        value,
        comparison,
        Boolean(absolute),
      );
    }

    if (absolute) {
      return t(
        "project.dashboard.block.diagram.above_absolute_threshold",
        value,
        comparison,
        columnUnit ?? "",
      );
    }

    if (comparison === "gte") {
      return t("project.dashboard.block.diagram.above_threshold", value, columnUnit ?? "");
    }

    if (comparison === "lte") {
      return t("project.dashboard.block.diagram.below_threshold", value, columnUnit ?? "");
    }

    return null;
  }, [matchingThreshold, columnUnit]);

  return { matchingThreshold, thresholdColor, thresholdDescription };
}
